import { useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DataTable,
  FormInput,
  useDataTable,
  type DataTableColumn,
} from "@/components/shared";
import {
  useCreateReferralLevelConfigMutation,
  useDeleteReferralLevelConfigMutation,
  usePlatformSettings,
  useReferralLevelConfigs,
  useToggleReferralLevelConfigMutation,
  useUpdateReferralLevelConfigMutation,
} from "@/hooks/queries";
import type { ReferralLevelConfigDto } from "@/types/referral.dto";
import { ReferralType } from "@/types/enums";
import { getApiErrorMessage } from "@/services/httpClient";
import { useToast } from "@/hooks/use-toast";

const emptyForm = {
  level: 1,
  commissionPercentage: 5,
  maxCommissionAmount: "",
  description: "",
};

const referralTypeLabel: Record<ReferralType, string> = {
  [ReferralType.User]: "Customer referrals",
  [ReferralType.Store]: "Store referrals",
};

export function ReferralLevelPanel({ type }: { type: ReferralType }) {
  const { toast } = useToast();
  const { data: configs = [], isLoading } = useReferralLevelConfigs(type);
  const { data: platformSettings } = usePlatformSettings();
  const createMutation = useCreateReferralLevelConfigMutation(type);
  const updateMutation = useUpdateReferralLevelConfigMutation(type);
  const deleteMutation = useDeleteReferralLevelConfigMutation(type);
  const toggleMutation = useToggleReferralLevelConfigMutation(type);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ReferralLevelConfigDto | null>(null);
  const [editing, setEditing] = useState<ReferralLevelConfigDto | null>(null);
  const [form, setForm] = useState(emptyForm);

  const levelBudget =
    type === ReferralType.User ? (platformSettings?.bv.levelCashbackPercent ?? 0) : null;

  const usedPercent = useMemo(
    () =>
      configs
        .filter((c) => c.isActive)
        .reduce((sum, c) => sum + Number(c.commissionPercentage || 0), 0),
    [configs],
  );

  const remainingPercent =
    levelBudget == null ? null : Number((levelBudget - usedPercent).toFixed(2));
  const isOverBudget = remainingPercent != null && remainingPercent < -0.01;

  const table = useDataTable<ReferralLevelConfigDto>({
    data: configs,
    searchKeys: ["level", "description"],
  });

  const openCreate = () => {
    setEditing(null);
    const nextLevel =
      configs.length === 0 ? 1 : Math.max(...configs.map((c) => c.level)) + 1;
    const suggested =
      remainingPercent != null && remainingPercent > 0
        ? Math.min(5, remainingPercent)
        : 5;
    setForm({ ...emptyForm, level: nextLevel, commissionPercentage: suggested });
    setDialogOpen(true);
  };

  const openEdit = (config: ReferralLevelConfigDto) => {
    setEditing(config);
    setForm({
      level: config.level,
      commissionPercentage: config.commissionPercentage,
      maxCommissionAmount: config.maxCommissionAmount != null ? String(config.maxCommissionAmount) : "",
      description: config.description ?? "",
    });
    setDialogOpen(true);
  };

  const projectedUsedPercent = () => {
    const others = configs
      .filter((c) => c.isActive && c.id !== editing?.id)
      .reduce((sum, c) => sum + Number(c.commissionPercentage || 0), 0);
    const thisPct = Number(form.commissionPercentage || 0);
    const includeThis = editing ? editing.isActive : true;
    return includeThis ? others + thisPct : others;
  };

  const handleSave = async () => {
    const commissionPercentage = Number(form.commissionPercentage);
    if (!Number.isFinite(commissionPercentage) || commissionPercentage <= 0) {
      toast({
        title: "Invalid commission %",
        description: "Commission percentage must be greater than 0.",
        variant: "destructive",
      });
      return;
    }

    if (levelBudget != null && commissionPercentage - levelBudget > 0.01) {
      toast({
        title: "Invalid commission %",
        description: `A single level cannot exceed platform Level income (${levelBudget}%).`,
        variant: "destructive",
      });
      return;
    }

    const willAdjust =
      levelBudget != null &&
      Math.abs(projectedUsedPercent() - levelBudget) > 0.01;

    if (willAdjust && projectedUsedPercent() - levelBudget > 0.01) {
      const projected = projectedUsedPercent();
      const ok = window.confirm(
        `Active levels would total ${projected.toFixed(2)}%, over the Level income budget of ${levelBudget}%.\n\nOther active levels will be adjusted so the total stays at ${levelBudget}%. Continue?`,
      );
      if (!ok) return;
    } else if (willAdjust && levelBudget - projectedUsedPercent() > 0.01) {
      // Under budget after edit — still auto-fill others; soft confirm optional, proceed silently
    }

    const dto = {
      level: form.level,
      commissionPercentage,
      maxCommissionAmount: form.maxCommissionAmount ? Number(form.maxCommissionAmount) : undefined,
      applicableFor: type,
      description: form.description || undefined,
    };

    try {
      if (editing?.id) {
        await updateMutation.mutateAsync({ id: editing.id, dto });
        toast({
          title: "Level config updated",
          description: willAdjust
            ? "Other active levels were auto-adjusted to fit the Level income budget."
            : undefined,
        });
      } else {
        await createMutation.mutateAsync(dto);
        toast({
          title: "Level config created",
          description: willAdjust
            ? "Other active levels were auto-adjusted to fit the Level income budget."
            : undefined,
        });
      }
      setDialogOpen(false);
    } catch (error) {
      toast({ title: "Could not save config", description: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast({
        title: "Level config deleted",
        description:
          type === ReferralType.User && levelBudget != null
            ? `Remaining active levels were auto-adjusted to total ${levelBudget}% Level income.`
            : undefined,
      });
      setDeleteTarget(null);
    } catch (error) {
      toast({ title: "Could not delete config", description: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handleToggle = async (config: ReferralLevelConfigDto, isActive: boolean) => {
    if (!config.id) return;

    if (isActive && levelBudget != null) {
      const projected =
        configs
          .filter((c) => c.isActive && c.id !== config.id)
          .reduce((sum, c) => sum + Number(c.commissionPercentage || 0), 0) +
        Number(config.commissionPercentage || 0);
      if (projected - levelBudget > 0.01) {
        const ok = window.confirm(
          `Enabling this level would total ${projected.toFixed(2)}%, over the ${levelBudget}% budget.\n\nOther active levels will be reduced proportionally. Continue?`,
        );
        if (!ok) return;
      }
    }

    try {
      await toggleMutation.mutateAsync({ id: config.id, isActive });
      toast({ title: isActive ? "Level enabled" : "Level disabled" });
    } catch (error) {
      toast({ title: "Could not update level", description: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const columns: DataTableColumn<ReferralLevelConfigDto>[] = useMemo(
    () => [
      {
        id: "level",
        header: "Level",
        accessorKey: "level",
        sortable: true,
        cell: (row) => <span className="font-semibold">Level {row.level}</span>,
      },
      {
        id: "commissionPercentage",
        header: "Commission % of BV",
        accessorKey: "commissionPercentage",
        sortable: true,
        align: "right",
        cell: (row) => `${row.commissionPercentage}%`,
      },
      {
        id: "maxCommissionAmount",
        header: "Max (₹)",
        align: "right",
        hideOnMobile: true,
        cell: (row) => (row.maxCommissionAmount != null ? `₹${row.maxCommissionAmount}` : "—"),
      },
      {
        id: "description",
        header: "Description",
        className: "text-sm text-muted-foreground max-w-xs truncate",
        hideOnMobile: true,
        cell: (row) => row.description ?? "—",
      },
      {
        id: "isActive",
        header: "Active",
        cell: (row) => (
          <Switch checked={row.isActive} onCheckedChange={(v) => handleToggle(row, v)} disabled={!row.id} />
        ),
      },
      {
        id: "actions",
        header: "Actions",
        align: "center",
        cell: (row) => (
          <div className="flex items-center justify-center gap-1">
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(row)}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => setDeleteTarget(row)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [configs, levelBudget],
  );

  if (isLoading) return <Skeleton className="h-64 rounded-xl" />;

  return (
    <>
      {levelBudget != null && (
        <Alert className={`mb-4 ${isOverBudget ? "border-destructive/50" : ""}`}>
          <AlertDescription className="text-sm">
            Level income budget from Platform Settings: <strong>{levelBudget}%</strong> of product BV.
            Active levels use <strong>{usedPercent.toFixed(2)}%</strong>
            {remainingPercent != null && !isOverBudget && (
              <>
                {" "}
                · remaining <strong>{Math.max(0, remainingPercent).toFixed(2)}%</strong>
              </>
            )}
            {isOverBudget && (
              <>
                {" "}
                · <strong className="text-destructive">over by {Math.abs(remainingPercent!).toFixed(2)}%</strong>
                {" "}(refresh page — API will auto-normalize to {levelBudget}%)
              </>
            )}
            . On <strong>add/edit</strong>, your level % is kept and other active levels are scaled so total = {levelBudget}%.
            On <strong>delete/disable</strong>, remaining active levels are scaled to fill {levelBudget}%.
          </AlertDescription>
        </Alert>
      )}

      <DataTable
        title={`${referralTypeLabel[type]} (${configs.length})`}
        columns={columns}
        data={table.rows}
        getRowId={(row) => String(row.id ?? row.level)}
        search={table.state.search}
        onSearchChange={table.setSearch}
        searchPlaceholder="Search levels..."
        sort={table.state.sort}
        onSortChange={table.toggleSort}
        pagination={table.pagination}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        emptyMessage="No referral levels configured yet. Click Add Level to create one."
        toolbarExtra={
          <Button size="sm" className="gap-1" onClick={openCreate}>
            <Plus className="h-4 w-4" /> Add Level
          </Button>
        }
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Referral Level" : "Add Referral Level"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <FormInput
              label="Level"
              type="number"
              value={form.level}
              onChange={(e) => setForm({ ...form, level: Number(e.target.value) })}
              disabled={Boolean(editing)}
            />
            <FormInput
              label="Commission % of product BV"
              type="number"
              value={form.commissionPercentage}
              onChange={(e) => setForm({ ...form, commissionPercentage: Number(e.target.value) })}
            />
            {levelBudget != null && (
              <p className="text-xs text-muted-foreground">
                Budget {levelBudget}% · after save (if active): {projectedUsedPercent().toFixed(2)}%
              </p>
            )}
            <FormInput
              label="Max commission (₹, optional)"
              type="number"
              value={form.maxCommissionAmount}
              onChange={(e) => setForm({ ...form, maxCommissionAmount: e.target.value })}
            />
            <FormInput
              label="Description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={createMutation.isPending || updateMutation.isPending}>
              <Save className="h-4 w-4 mr-1" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete level {deleteTarget?.level}?</AlertDialogTitle>
            <AlertDialogDescription>
              Referral commissions will no longer be calculated for this level.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
